import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog } from "antd-mobile";
import {
  getGroupTourOrderDetail,
  cancelGroupTourOrder,
  getGroupTourRefundList,
  getGroupTourRefundDetail,
  cancelGroupTourRefund,
} from "../../route-detail-product/api/order";
import { type TourOrderDetail } from "@/api/tourOrder";
import type { RefundInfo } from "@/api/order";
import { toastError, toastSuccess, toastWarning } from "@/utils/toast";

// ── Components ──────────────────────────────────────────────
import { ProgressBarByStep } from "@/components/ProgressBar";
import ProductActionButtons from "./components/ProductActionButtons";
import InvoiceStatusNotice from "../components/InvoiceStatusNotice";

// ── Sections ────────────────────────────────────────────────
import TourProductSection from "./sections/TourProductSection";
import ItinerarySection from "./sections/ItinerarySection";

// ── Shared Modals ──────────────────────────────────────────
import { AfterSaleModal, ReviewModal, InvoiceModal } from "@/components/modals";
import RefundInfoSection from "../components/RefundInfoSection";
import VoucherServiceSection from "../components/VoucherServiceSection";

function resolveMainStatusStep(
  status: number,
  hasRefund: boolean,
  refundStatus: number,
) {
  if ((hasRefund && refundStatus !== 30) || status === 6 || status === 5) {
    return -1;
  }
  let step = 0;
  if (status <= 4 || status === 7) {
    return status;
  }
  return step;
}

/** 将团游 API 数据映射为 UI 所需格式 */
function mapTourOrderToUI(data: TourOrderDetail, refundInfo?: RefundInfo | null) {
  const raw = data as Record<string, unknown>;
  const totalPeople = (raw.total_people as number) || 0;

  return {
    id: String(data.id),
    orderNo: data.order_no,
    addTime: (raw.add_time as string) || "",
    title: data.product?.title ?? "线路订单",
    coverImage: data.product?.cover ?? data.product?.cover_image,
    days: data.days,
    nights: data.nights,
    tags: ["线路"],
    startDate: (raw.start_date as string) || "",
    endDate: (raw.end_date as string) || "",
    travelers: totalPeople,
    travelersText: `${totalPeople}人`,
    pickup: data.departure_city,
    contactName: data.contact?.name ?? "",
    contactPhone: data.contact?.phone ?? "",
    contactEmail: data.contact?.email ?? "",
    roomType: ((raw.room as Record<string, unknown>)?.type as string) || "",
    roomUnitPrice: ((raw.room as Record<string, unknown>)?.unit_price as string) || "",
    total: Number(data.total_amount ?? 0),
    status: data.status_text,
    step: resolveMainStatusStep(data.status, false, 0),
    subStatus: data.status_text ?? "未知",
    destinations: data.destinations,
    tripDescription: (raw.trip_description as string) ?? "",
    itinerary: data.product?.itinerary,
    hasComment: 0,
    hasInvoice: 0,
    invoiceStatus: null,
    hasRefund: refundInfo ? 1 : 0,
    refundStatus: refundInfo?.refund_status ?? 0,
    refundInfo,
  };
}

// ─── Main Page ─────────────────────────────────────────────
export default function OrderDetailTourPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as {
    orderId?: number;
    productType?: string;
  } | null;
  const orderId = locState?.orderId;

  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<ReturnType<
    typeof mapTourOrderToUI
  > | null>(null);
  const [rawData, setRawData] = useState<TourOrderDetail | null>(null);
  const [modal, setModal] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);

    const loadOrderDetail = async () => {
      try {
        const res = await getGroupTourOrderDetail(orderId);
        if (res.data.code !== 200) throw new Error(res.data.message || "获取订单失败");
        const orderData = res.data.data;
        setRawData(orderData);

        // 查询团游售后记录并展示
        let refundInfo: RefundInfo | null = null;
        try {
          const listRes = await getGroupTourRefundList({ limit: 100 });
          if (listRes.data.code === 200) {
            const match = listRes.data.data.list.find((item) => item.order_id === orderId);
            if (match?.refund_id) {
              const detailRes = await getGroupTourRefundDetail(match.refund_id);
              if (detailRes.data.code === 200) {
                refundInfo = detailRes.data.data;
              }
            }
          }
        } catch (e) {
          console.error("[售后详情] 加载失败:", e);
        }

        setOrder(mapTourOrderToUI(orderData, refundInfo));
      } catch (err: any) {
        console.error(err);
        toastError(err?.message || "加载失败");
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetail();
  }, [orderId]);

  const handleAction = (action: string) => {
    if (action === "cancelRefund") {
      const refundId = order?.refundInfo?.refund_id;
      if (!refundId) {
        toastError("未找到售后记录");
        return;
      }
      Dialog.show({
        content: "确认要取消该售后申请吗？",
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "再想想",
              style: { color: "#FF6B35", fontWeight: 500 },
              onClick: () => {},
            },
            {
              key: "confirm",
              text: "确认取消",
              bold: true,
              style: { color: "#FF6B35", fontWeight: 600 },
              onClick: async () => {
                try {
                  await cancelGroupTourRefund({ refund_id: refundId });
                  toastSuccess("售后申请已取消");
                  window.location.reload();
                } catch (err: any) {
                  toastError(err?.message || "取消售后失败");
                }
              },
            },
          ],
        ],
      });
      return;
    }
    if (action === "cancel") {
      Dialog.show({
        content: `确认取消订单 ${order?.orderNo || ""}？取消后无法恢复。`,
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "再想想",
              style: { color: "#FF6B35", fontWeight: 500 },
              onClick: () => {},
            },
            {
              key: "confirm",
              text: "确认取消",
              bold: true,
              style: { color: "#FF6B35", fontWeight: 600 },
              onClick: async () => {
                try {
                  await cancelGroupTourOrder({ id: orderId!, reason: "用户主动取消" });
                  toastSuccess("订单已取消");
                  window.location.reload();
                } catch (err: any) {
                  toastError(err?.message || "取消失败");
                }
              },
            },
          ],
        ],
      });
      return;
    }
    setModal(action);
  };

  // Loading 状态
  if (!order && loading)
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F4F5F9" }}
      >
        <div className="flex flex-col items-center gap-3">
          <i
            className="ri-loader-4-line animate-spin"
            style={{ fontSize: 32, color: "#FF6B35" }}
          />
          <span className="text-gray-400">加载中...</span>
        </div>
      </div>
    );

  if (!order && !loading)
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F4F5F9" }}
      >
        <div className="text-center">
          <p className="text-gray-400 mb-2">无法加载订单信息</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 rounded-full text-sm font-medium cursor-pointer"
            style={{ background: "#FF6B35", color: "#fff" }}
          >
            返回
          </button>
        </div>
      </div>
    );

  const getStatusNote = (status: string) => {
    switch (status) {
      case "待出行":
        return "所有服务待使用";
      case "全部履约":
        return "所有服务已完成，待结算";
      case "已完成":
        return "订单已结算，可评价";
      case "全额取消":
        return "订单已全额取消，可查看详情";
      case "售后中":
        return "售后处理中，可跟进";
      case "待商家确认":
        return "商家正在审核，预计1-2小时内确认";
      default:
        return null;
    }
  };
  const statusNote = getStatusNote(order!.status);

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F4F5F9",
        fontFamily: "'Inter','SF Pro Text',system-ui,sans-serif",
      }}
    >
      {/* ── 深色 Header ── */}
      <div
        className=" overflow-hidden sticky top-0 z-40"
        style={{
          background:
            "linear-gradient(145deg,#1A2238 0%,#2D3561 60%,#3D5A80 100%)",
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              width: 200,
              height: 200,
              top: -50,
              right: -30,
              background: "rgba(255,107,53,0.1)",
              filter: "blur(40px)",
            }}
          />
        </div>
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <i
                className="ri-arrow-left-line"
                style={{ fontSize: 18, color: "#fff" }}
              />
            </button>
            <div className="flex-1 min-w-0">
              <h1
                className="font-bold text-white leading-snug"
                style={{ fontSize: 15 }}
              >
                {order!.destinations || "线路"}线路订单
                <span
                  className="font-normal ml-1.5"
                  style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}
                >
                  {" "}
                  · 订单号：{order!.id}
                </span>
              </h1>
              <div
                className="mt-0.5"
                style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}
              >
                创建时间：{order!.addTime} | 总金额：
                <span style={{ color: "#FCA5A5", fontWeight: 600 }}>
                  ¥{order!.total.toLocaleString()}
                </span>
              </div>
            </div>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              {order!.status}
            </span>
          </div>
          {statusNote && (
            <div
              className="ml-12 mb-1.5 text-xs"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {statusNote}
            </div>
          )}
          <ProgressBarByStep
            step={order!.step}
            titles={["待支付", "待确认", "待签约", "待出行", "已完成"]}
            theme="orange"
          />
        </div>
      </div>

      <InvoiceStatusNotice
        hasInvoice={order!.hasInvoice}
        invoiceStatus={order!.invoiceStatus}
      />

      {/* 线路侧状态提醒 */}
      {(order!.subStatus === "行程中" || order!.status === "待商家确认") && (
        <div className="mx-4 mt-3 bg-white rounded-2xl p-4">
          {order!.status === "待商家确认" && (
            <div
              className="mt-3 px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
            >
              <i
                className="ri-time-line flex-shrink-0"
                style={{ color: "#EA580C", fontSize: 14 }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: "#EA580C" }}
              >
                商家正在审核，预计1-2小时内确认
              </span>
            </div>
          )}
          {order!.subStatus === "行程中" && (
            <div
              className="mt-3 px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
            >
              <i
                className="ri-map-pin-time-line flex-shrink-0"
                style={{ color: "#059669", fontSize: 14 }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: "#059669" }}
              >
                行程进行中，祝您旅途愉快！
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── 产品内容 ── */}
      <TourProductSection order={order!} onAction={handleAction} />
      <ItinerarySection list={order!.itinerary} />

      {/* ── 费用明细 ── */}
      <div className="mx-4 mt-3 bg-white rounded-2xl overflow-hidden">
        <div className="w-full flex items-center justify-between px-4 py-3.5 cursor-default">
          <span className="text-sm font-bold text-gray-900">费用明细</span>
          <div className="flex items-center gap-2">
            <span className="font-bold text-base" style={{ color: "#EF4444" }}>
              ¥{Number(order!.total).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* ── 售后信息：has_refund=1 且 refund_status 为 0 或 30 时不展示，其余展示 ── */}
      {order?.hasRefund === 1 &&
        order?.refundInfo &&
        order.refundStatus != null &&
        order.refundStatus !== 0 &&
        order.refundStatus !== 30 && (
          <RefundInfoSection refundInfo={order.refundInfo} />
        )}

      <VoucherServiceSection
        onNavigate={(path) => navigate(path)}
        items={[
          {
            icon: "ri-file-text-line",
            label: "电子合同",
            sub: "待签署",
            color: "#2563EB",
            path: "/contracts",
          },
          {
            icon: "ri-bill-line",
            label: "电子发票",
            sub: "可申请开具",
            color: "#D97706",
            path: "/invoices",
          },
          {
            icon: "ri-customer-service-2-line",
            label: "在线客服",
            sub: "7×24小时",
            color: "#7C3AED",
            path: "/",
          },
        ]}
      />

      {/* ── 底部操作 ── */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white px-4 z-40 flex gap-3"
        style={{ borderTop: "1px solid #F5F5F5", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <ProductActionButtons
          productType="tour"
          hasComment={order!.hasComment}
          mainStatus={order!.status}
          mainStatusStep={order!.step}
          hasRefund={order!.hasRefund}
          refundStatus={order!.refundStatus}
          hasInvoice={Number(order!.hasInvoice) || 0}
          onAction={handleAction}
          onNavigate={(p) => navigate(p)}
          onReorder={() => {
            // 传递产品ID → 下单页会调用 preCreateTourOrder API 获取真实数据
            const routeId = rawData?.product?.id;
            if (!routeId) {
              toastWarning("线路信息加载中，请稍后再试");
              return;
            }
            navigate("/route-detail-product", {
              state: {
                routeId,
              },
            });
          }}
        />
      </div>

      {/* ── 弹窗 ── */}
      {modal === "afterSale" && (
        <AfterSaleModal
          visible={true}
          onClose={() => setModal(null)}
          productName={order!.title}
          orderId={orderId}
          orderType="tour"
          orderAmount={order!.total}
        />
      )}
      {modal === "review" && (
        <ReviewModal
          visible={true}
          onClose={() => setModal(null)}
          orderId={orderId}
          productName={order!.title}
          productId={rawData?.product?.id}
          productType="tour"
        />
      )}
      {modal === "invoice" && orderId != null && (
        <InvoiceModal
          orderId={orderId}
          productType="tour"
          productTitle={order!.title}
          productSubtitle={[
            order!.tripDescription,
            order!.days != null
              ? `${order!.days}天${order!.nights != null ? `${order!.nights}晚` : ""}`
              : "",
            order!.travelersText,
          ]
            .filter((s) => s && String(s).trim())
            .join(" · ")}
          invoiceAmount={order!.total}
          onClose={() => setModal(null)}
          onSubmitSuccess={() => setModal(null)}
        />
      )}
    </div>
  );
}
